/**
 * `useProjectTransactions` / `useTransaction` — live reads of
 * transactions on the top-level `transactions` collection.
 *
 * Mirrors the mobile hooks at
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/transactions/useTransactions.ts
 *
 * Schema parity with the mobile write helpers — same field names so
 * docs written by either client read cleanly on the other.
 *
 * Role-aware scope:
 *   - Submit-only roles (Site Engineer / Supervisor) MUST filter by
 *     `createdBy == uid` because the Firestore rule restricts their
 *     transaction reads to their own submissions. Without the
 *     per-creator filter, the rule rejects the query entirely and
 *     the user sees a "permission-denied" empty state instead of
 *     their pending submissions. `isSubmitOnlyTxnRole(role)` from
 *     `@shared/perms` is the same predicate the mobile hook uses,
 *     so both surfaces converge on the same scoping rule.
 */
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { canSeeAnyTransactions, isSubmitOnlyTxnRole } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { db } from '@/lib/firebase';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';

export type TransactionItem = {
  id: string;
  orgId: string;
  projectId: string;
  /** 'payment_in' or 'payment_out' (legacy: 'income' / 'expense'). */
  type?: string;
  amount: number;
  description?: string;
  partyId?: string;
  partyName?: string;
  category?: string;
  paymentMethod?: string;
  referenceNumber?: string;
  status?: 'paid' | 'pending' | 'partial';
  /** Mobile writes `date` for the transaction date. */
  date?: { toMillis(): number } | null;
  /** Legacy alias seen on some older docs. */
  transactionDate?: { toMillis(): number } | null;
  workflowStatus?: 'posted' | 'pending_approval' | 'rejected';
  submissionKind?: 'expense_reimbursement' | 'party_payment';
  rejectionNote?: string;
  notes?: string;
  /** Receipt photos — first single-file fields are legacy, photoUrls
   *  is the modern multi-file array. */
  photoUrl?: string;
  photoStoragePath?: string;
  photoUrls?: string[];
  photoStoragePaths?: string[];
  /** Settlement (bank reconciliation) sub-object. */
  settlement?: {
    clearedToParty?: boolean;
    payeeLabel?: string;
    note?: string;
    recordedAt?: { toMillis(): number } | null;
    recordedBy?: string;
    clearedAt?: { toMillis(): number } | null;
    clearedBy?: string;
    settlementPhotoUrl?: string;
    settlementPhotoStoragePath?: string;
  };
  createdAt?: { toMillis(): number } | null;
  createdBy?: string;
  submittedAt?: { toMillis(): number } | null;

  // ─── Staff advance plumbing ───
  // Set on the parent advance (category === 'staff_advance').
  // 'open' = no bills filed, 'partial' = at least one bill but
  // balance non-zero, 'settled' = balance reconciled + closed.
  advanceStatus?: 'open' | 'partial' | 'settled';
  advanceClosedAt?: { toMillis(): number } | null;
  // Set on every CHILD (settlement / refund / reimbursement) —
  // points at the parent advance doc id so we can group + sum.
  parentTransactionId?: string;
  // 'settlement' = a real expense paid from the advance,
  // 'refund'     = engineer returned leftover cash (payment_in),
  // 'reimbursement' = studio paid engineer's overspend back out.
  advanceRole?: 'settlement' | 'refund' | 'reimbursement';
};

export type UseProjectTransactionsResult = {
  transactions: TransactionItem[];
  loading: boolean;
  error: string | null;
};

export function useProjectTransactions(
  orgId: string | null,
  projectId: string | undefined,
): UseProjectTransactionsResult {
  const { user } = useAuth();
  const { role: orgRole } = useCurrentOrgRole();
  const [items, setItems] = useState<TransactionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Submit-only roles must filter by `createdBy == uid` or the
  // Firestore rule will reject the read. Computed inline so the
  // useEffect dependency tracks role transitions cleanly.
  const scopeToOwn = isSubmitOnlyTxnRole(orgRole);
  const hasAnyTxnAccess = canSeeAnyTransactions(orgRole);
  const uid = user?.uid ?? null;

  useEffect(() => {
    if (!orgId || !projectId) {
      setItems([]);
      setLoading(false);
      return;
    }
    // Roles with ZERO transaction access (Designer today) would fire
    // a query the Firestore rule rejects in full. Skip the listener
    // entirely.
    if (!hasAnyTxnAccess) {
      setItems([]);
      setLoading(false);
      return;
    }
    // For submit-only roles, hold off on attaching the listener
    // until uid is known. Without uid we'd attach an unscoped
    // listener that the rule immediately denies.
    if (scopeToOwn && !uid) {
      setItems([]);
      setLoading(true);
      return;
    }
    setLoading(true);
    setError(null);
    const constraints = [
      where('orgId', '==', orgId),
      where('projectId', '==', projectId),
    ];
    if (scopeToOwn && uid) {
      constraints.push(where('createdBy', '==', uid));
    }
    return onSnapshot(
      query(collection(db, 'transactions'), ...constraints),
      (snap) => {
        setItems(snap.docs.map((d) => ({ ...(d.data() as TransactionItem), id: d.id })));
        setLoading(false);
      },
      (err) => {
        console.warn('[useProjectTransactions] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [orgId, projectId, scopeToOwn, hasAnyTxnAccess, uid]);

  const sorted = useMemo(
    () =>
      [...items].sort((a, b) => {
        const am = txDateMs(a);
        const bm = txDateMs(b);
        return bm - am;
      }),
    [items],
  );

  return { transactions: sorted, loading, error };
}

/** Single-doc subscription. Used by the preview slide-over. */
export function useTransaction(txnId: string | undefined): {
  transaction: TransactionItem | null;
  loading: boolean;
  error: string | null;
} {
  const [tx, setTx] = useState<TransactionItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!txnId) {
      setTx(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      doc(db, 'transactions', txnId),
      (snap) => {
        if (!snap.exists()) setTx(null);
        else setTx({ ...(snap.data() as TransactionItem), id: snap.id });
        setLoading(false);
      },
      (err) => {
        console.warn('[useTransaction] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [txnId]);

  return { transaction: tx, loading, error };
}

// ───── Pure utilities ─────

export function txDateMs(t: TransactionItem): number {
  return t.date?.toMillis?.() ?? t.transactionDate?.toMillis?.() ?? t.createdAt?.toMillis?.() ?? 0;
}

/** Mobile uses 'payment_in'/'payment_out' but a few legacy docs are
 *  'income'/'expense'. */
export function normaliseTxType(raw: string | undefined): 'in' | 'out' | null {
  if (!raw) return null;
  if (raw === 'payment_in' || raw === 'income') return 'in';
  if (raw === 'payment_out' || raw === 'expense') return 'out';
  return null;
}

/** Cash-flow gate — should this txn count toward the project's
 *  income / expense / balance totals?
 *
 *  Workflow check: pending/rejected entries are excluded; legacy
 *  docs without `workflowStatus` count (back-compat).
 *
 *  Advance-hierarchy check: settlement children (bills filed
 *  against a parent advance) DO NOT count, because the cash
 *  already left the studio when the parent advance was recorded.
 *  Bills are just labels saying HOW the advance was spent —
 *  including them would double-count the cash. The parent advance
 *  + any reimbursement children + any refund children are the
 *  real cash events and DO count. */
export function isCountedInTotals(t: TransactionItem): boolean {
  const w = t.workflowStatus;
  if (w != null && w !== 'posted') return false;
  // Skip bills filed against a parent advance — they don't move
  // cash. See `lib/transactions.ts` for the full advance domain
  // model; the helper here is the read-side gate.
  if (t.parentTransactionId && t.advanceRole === 'settlement')
    return false;
  return true;
}

/** Category-report gate — should this txn appear in a
 *  "spend by category" breakdown?
 *
 *  Different rules from cash-flow because the category report
 *  answers a different question: "what was bought, by category?"
 *  not "when did cash move?". So:
 *
 *  - Parent advances are SKIPPED — their category is the
 *    placeholder 'staff_advance' (a wallet, not a real expense
 *    category). The REAL categories live on the settlement
 *    children below.
 *  - Refunds are SKIPPED — they're income (cash in), not spend.
 *  - Reimbursements are SKIPPED — they look like an expense
 *    (`payment_out`), but the actual spend they reimburse is
 *    already captured in the settlement bill the engineer filed
 *    using his own money. Counting them here would double-count
 *    the spend (once in the bill's real category, again as
 *    'others' via the reimbursement).
 *  - Settlements + every regular payment_out ARE counted. */
export function isCountedInCategoryReport(t: TransactionItem): boolean {
  const w = t.workflowStatus;
  if (w != null && w !== 'posted') return false;
  if (t.category === 'staff_advance' && !t.parentTransactionId)
    return false;
  if (t.advanceRole === 'refund') return false;
  if (t.advanceRole === 'reimbursement') return false;
  return true;
}
